/**
 * SUPPLY STUDIOS - Main Theme JavaScript
 * Vanilla JS - NO jQuery for performance
 * Deferred loading for optimal performance
 */

// ===== UTILITY FUNCTIONS =====

/**
 * Debounce function for performance
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in ms
 * @returns {Function} Debounced function
 */
function debounce(func, wait = 100) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle function for performance
 * @param {Function} func - Function to throttle
 * @param {number} limit - Limit in ms
 * @returns {Function} Throttled function
 */
function throttle(func, limit = 100) {
  let inThrottle;
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Fetch wrapper with error handling
 * @param {string} url - URL to fetch
 * @param {Object} options - Fetch options
 * @returns {Promise} Fetch promise
 */
async function fetchJSON(url, options = {}) {
  try {
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Fetch error:', error);
    throw error;
  }
}

// ===== CART MODULE =====

const Cart = {
  /**
   * Initialize cart functionality
   */
  init() {
    this.bindEvents();
    this.updateCartCount();
  },

  /**
   * Bind cart-related events
   */
  bindEvents() {
    // Event delegation for add to cart buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-add-to-cart]') || e.target.closest('[data-add-to-cart]')) {
        e.preventDefault();
        const button = e.target.matches('[data-add-to-cart]') ? e.target : e.target.closest('[data-add-to-cart]');
        const variantId = button.dataset.variantId;
        if (variantId) {
          this.addItem(variantId);
        }
      }

      // Remove from cart
      if (e.target.matches('[data-cart-remove]') || e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        const button = e.target.matches('[data-cart-remove]') ? e.target : e.target.closest('[data-cart-remove]');
        const lineItem = button.dataset.lineItem;
        if (lineItem) {
          this.removeItem(lineItem);
        }
      }
    });
  },

  /**
   * Add item to cart via AJAX
   * @param {string|number} variantId - Variant ID
   * @param {number} quantity - Quantity to add
   */
  async addItem(variantId, quantity = 1) {
    try {
      const data = await fetchJSON('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({
          id: variantId,
          quantity: quantity,
        }),
      });

      // Update cart count and refresh drawer contents
      await this.updateCartCount();
      await this.refreshCartDrawer();

      // Open cart drawer if it exists
      this.openCartDrawer();

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

      return data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      alert('Error adding item to cart. Please try again.');
    }
  },

  /**
   * Remove item from cart
   * @param {number} lineItem - Line item number
   */
  async removeItem(lineItem) {
    try {
      await fetchJSON('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({
          line: lineItem,
          quantity: 0,
        }),
      });

      // Update cart
      await this.updateCartCount();
      await this.refreshCartDrawer();

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('cart:item-removed'));
    } catch (error) {
      console.error('Error removing from cart:', error);
      alert('Error removing item from cart. Please try again.');
    }
  },

  /**
   * Update cart count in header
   */
  async updateCartCount() {
    try {
      const cart = await fetchJSON('/cart.js');
      const countElements = document.querySelectorAll('[data-cart-count]');

      countElements.forEach((element) => {
        // Number shows INSIDE the cart icon; empty cart -> no number at all
        // (not "0"). Keep the .hidden toggle too as a belt-and-suspenders hide.
        element.textContent = cart.item_count > 0 ? cart.item_count : '';
        element.classList.toggle('hidden', cart.item_count === 0);
      });

      return cart;
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  },

  /**
   * Open cart drawer
   */
  openCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');

    if (drawer) {
      drawer.classList.add('open');
      if (overlay) {
        overlay.classList.add('active');
      }
      document.body.style.overflow = 'hidden';
    }
  },

  /**
   * Close cart drawer
   */
  closeCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');

    if (drawer) {
      drawer.classList.remove('open');
      if (overlay) {
        overlay.classList.remove('active');
      }
      document.body.style.overflow = '';
    }
  },

  /**
   * Refresh cart drawer content
   */
  async refreshCartDrawer() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    try {
      const cart = await fetchJSON('/cart.js');

      // Update cart count
      await this.updateCartCount();

      // Get cart drawer HTML via AJAX
      const response = await fetch('/?sections=cart-drawer');
      const data = await response.json();

      // Parse the cart drawer section HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(data['cart-drawer'], 'text/html');

      // Replace entire drawer inner content
      const newDrawerInner = doc.querySelector('.cart-drawer-inner');
      const currentDrawerInner = drawer.querySelector('.cart-drawer-inner');

      if (newDrawerInner && currentDrawerInner) {
        currentDrawerInner.innerHTML = newDrawerInner.innerHTML;
      }

      // Update cart count in header (empty cart -> no number, not "0")
      const headerCount = document.querySelectorAll('[data-cart-count]');
      headerCount.forEach(el => {
        el.textContent = cart.item_count > 0 ? cart.item_count : '';
        el.classList.toggle('hidden', cart.item_count === 0);
      });

      // Update mobile cart text
      const cartText = document.querySelector('[data-cart-text]');
      if (cartText) {
        cartText.textContent = cart.item_count > 0 ? `CART (${cart.item_count})` : 'CART';
      }

    } catch (error) {
      console.error('Error refreshing cart:', error);
    }
  },
};

// ===== MOBILE NAVIGATION =====

const MobileNav = {
  /**
   * Initialize mobile navigation
   */
  init() {
    this.bindEvents();
  },

  /**
   * Bind mobile nav events
   */
  bindEvents() {
    // Menu toggle
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-menu-toggle]') || e.target.closest('[data-menu-toggle]')) {
        e.preventDefault();
        this.toggleMenu();
      }

      // Close menu when clicking overlay
      if (e.target.matches('[data-nav-overlay]')) {
        this.closeMenu();
      }
    });

    // Close menu on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeMenu();
      }
    });
  },

  /**
   * Toggle mobile menu
   */
  toggleMenu() {
    const drawer = document.querySelector('[data-mobile-nav]');
    const overlay = document.querySelector('[data-nav-overlay]');

    if (!drawer) return;

    const isOpen = drawer.classList.contains('open');

    if (isOpen) {
      this.closeMenu();
    } else {
      drawer.classList.add('open');
      if (overlay) {
        overlay.classList.add('active');
      }
      document.body.style.overflow = 'hidden';
    }
  },

  /**
   * Close mobile menu
   */
  closeMenu() {
    const drawer = document.querySelector('[data-mobile-nav]');
    const overlay = document.querySelector('[data-nav-overlay]');

    if (drawer) {
      drawer.classList.remove('open');
      if (overlay) {
        overlay.classList.remove('active');
      }
      document.body.style.overflow = '';
    }
  },
};

// ===== SEARCH (Inline Header Search) =====

const Search = {
  headerContainer: null,
  headerCenter: null,
  headerSearch: null,
  searchInput: null,
  searchResults: null,
  searchOverlay: null,
  debounceTimer: null,
  isOpen: false,

  /**
   * Initialize search functionality
   */
  init() {
    this.headerContainer = document.querySelector('.header-container');
    this.headerCenter = document.querySelector('[data-header-center]');
    this.headerSearch = document.querySelector('[data-header-search]');
    this.searchInput = document.querySelector('[data-header-search-input]');
    this.searchResults = document.querySelector('[data-header-search-results]');
    this.searchOverlay = document.querySelector('[data-header-search-overlay]');

    this.bindEvents();
  },

  /**
   * Bind search events
   */
  bindEvents() {
    // Search toggle (open search)
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-search-toggle]') || e.target.closest('[data-search-toggle]')) {
        e.preventDefault();
        this.openSearch();
      }

      // Close search via X button
      if (e.target.matches('[data-header-search-close]') || e.target.closest('[data-header-search-close]')) {
        e.preventDefault();
        this.closeSearch();
      }

      // Close when clicking overlay
      if (e.target.matches('[data-header-search-overlay]')) {
        this.closeSearch();
      }

      // Close when clicking outside search area (but not on results)
      if (this.isOpen && !e.target.closest('[data-header-search]') && !e.target.closest('[data-search-toggle]')) {
        this.closeSearch();
      }
    });

    // Search input - debounced predictive search
    if (this.searchInput) {
      this.searchInput.addEventListener('input', (e) => {
        clearTimeout(this.debounceTimer);
        const query = e.target.value.trim();

        if (query.length >= 2) {
          this.debounceTimer = setTimeout(() => {
            this.performPredictiveSearch(query);
          }, 300);
        } else {
          this.searchSeq = (this.searchSeq || 0) + 1; // drop any search still in flight
          this.hideResults();
        }
      });

      // Prevent clicks inside search from closing it
      this.searchInput.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeSearch();
      }
    });
  },

  /**
   * Open inline search in header
   */
  openSearch() {
    if (!this.headerCenter || !this.headerSearch) return;

    // Add search-active to both container (for grid) and center (for logo)
    if (this.headerContainer) {
      this.headerContainer.classList.add('search-active');
    }
    this.headerCenter.classList.add('search-active');
    this.headerSearch.classList.add('active');
    this.isOpen = true;

    // Show overlay
    if (this.searchOverlay) {
      this.searchOverlay.classList.add('active');
    }

    // Focus on search input
    if (this.searchInput) {
      setTimeout(() => this.searchInput.focus(), 100);
    }
  },

  /**
   * Close inline search
   */
  closeSearch() {
    if (!this.headerCenter || !this.headerSearch) return;

    // Remove search-active from both container and center
    if (this.headerContainer) {
      this.headerContainer.classList.remove('search-active');
    }
    this.headerCenter.classList.remove('search-active');
    this.headerSearch.classList.remove('active');
    this.isOpen = false;

    // Hide overlay
    if (this.searchOverlay) {
      this.searchOverlay.classList.remove('active');
    }

    // Hide results
    this.hideResults();

    // Clear search input
    if (this.searchInput) {
      this.searchInput.value = '';
    }
  },

  /**
   * Perform predictive search using the CLIENT-SIDE INDEX (window.SupplySearch),
   * NOT Shopify's /search/suggest.json. The native predictive endpoint shares
   * the same relevance engine that drops borderline title matches, so it could
   * miss real products (verified). The index matches brand, title, category,
   * tags and sizes with typo correction (rules in snippets/search-index.liquid),
   * identical to the full results page — the dropdown is the top slice of the
   * same result set, with the full count.
   * Falls back to Shopify's suggest API only if the index is unavailable.
   */
  async performPredictiveSearch(query) {
    // Each keystroke's search is async; only the LATEST one may paint, or a
    // slow earlier search ("ch") could overwrite a newer one ("chrome h").
    const seq = (this.searchSeq = (this.searchSeq || 0) + 1);
    try {
      if (window.SupplySearch && typeof window.SupplySearch.search === 'function') {
        const matches = await window.SupplySearch.search(query);
        if (seq !== this.searchSeq) return;
        // Map index entries to the shape displayResults() expects (price in
        // cents for theme.js formatMoney; image as featured_image). Top 6.
        const products = matches.slice(0, 6).map(function(p) {
          return {
            url: p.url,
            title: p.title,
            vendor: p.vendor,
            featured_image: p.image,
            price: p.price != null ? Math.round(p.price * 100) : 0
          };
        });
        this.displayResults({ resources: { results: { products: products } } }, query, matches.length, matches.corrected);
        return;
      }
      // Fallback: Shopify predictive API if the index isn't loaded.
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,vendor,body,product_type,tag`);
      const data = await response.json();
      if (seq !== this.searchSeq) return;
      this.displayResults(data, query);
    } catch (error) {
      console.error('Search error:', error);
      this.hideResults();
    }
  },

  /**
   * Display search results in dropdown
   */
  displayResults(data, query, total, corrected) {
    if (!this.searchResults || !data.resources || !data.resources.results) {
      return;
    }

    const products = data.resources.results.products || [];

    if (products.length === 0) {
      this.showNoResults(query);
      return;
    }

    // Show results container
    this.searchResults.classList.add('active');

    // The dropdown shows the top 6, but the count is the FULL match count
    // (it used to say "6 results" for a 140-result search). When the engine
    // corrected a typo, say what it searched for instead.
    const count = total != null ? total : products.length;
    const esc = (v) => String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const countLine = corrected
      ? `Showing results for "${esc(corrected)}"`
      : `${count} result${count !== 1 ? 's' : ''}`;

    // Build results HTML
    const resultsHTML = `
      <div class="search-results-count">
        ${countLine}
      </div>
      <div class="search-results-grid">
        ${products.map(product => `
          <a href="${esc(product.url)}" class="search-result-item">
            <div class="search-result-image">
              ${product.featured_image ? `
                <img src="${esc(product.featured_image)}" alt="${esc(product.title)}" loading="lazy" />
              ` : ''}
            </div>
            <div class="search-result-info">
              ${product.vendor ? `<div class="search-result-brand">${esc(product.vendor)}</div>` : ''}
              <h3 class="search-result-title">${esc(product.title)}</h3>
              <div class="search-result-price">${this.formatMoney(product.price)}</div>
            </div>
          </a>
        `).join('')}
      </div>
      <div class="search-results-view-all">
        <a href="/search?q=${encodeURIComponent(query)}">View All ${count} Results</a>
      </div>
    `;

    this.searchResults.innerHTML = resultsHTML;
  },

  /**
   * Show no results message
   */
  showNoResults(query) {
    if (!this.searchResults) return;

    this.searchResults.classList.add('active');
    this.searchResults.innerHTML = `
      <div class="search-no-results">
        No results found for "${String(query).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}"
      </div>
    `;
  },

  /**
   * Hide results dropdown
   */
  hideResults() {
    if (this.searchResults) {
      this.searchResults.classList.remove('active');
      this.searchResults.innerHTML = '';
    }
  },

  /**
   * Format money for display
   */
  formatMoney(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  },
};

// ===== FILTERS MODULE =====

const Filters = {
  /**
   * Initialize filter functionality
   */
  init() {
    this.filterPanel = document.querySelector('[data-filter-panel]');
    this.filterDrawer = document.querySelector('[data-filter-drawer]');
    this.filterOverlay = document.querySelector('[data-filter-overlay]');
    this.productGrid = document.querySelector('#product-grid');
    this.activeFilters = {};

    if (!this.productGrid) return;

    this.bindEvents();
    this.parseUrlFilters();
    this.updateUI();
  },

  /**
   * Bind filter-related events
   */
  bindEvents() {
    // Desktop: Filter toggle
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-filter-toggle]') || e.target.closest('[data-filter-toggle]')) {
        e.preventDefault();
        this.toggleFilterPanel();
      }

      // Mobile: Filter drawer toggle
      if (e.target.matches('[data-mobile-filter-toggle]') || e.target.closest('[data-mobile-filter-toggle]')) {
        e.preventDefault();
        this.openFilterDrawer();
      }

      // Close filter drawer
      if (e.target.matches('[data-filter-drawer-close]') || e.target.closest('[data-filter-drawer-close]')) {
        e.preventDefault();
        this.closeFilterDrawer();
      }

      // Apply filters (mobile)
      if (e.target.matches('[data-apply-filters]') || e.target.closest('[data-apply-filters]')) {
        e.preventDefault();
        this.closeFilterDrawer();
        this.applyFilters();
      }

      // Clear all filters
      if (e.target.matches('[data-clear-all]') || e.target.closest('[data-clear-all]')) {
        e.preventDefault();
        this.clearAllFilters();
      }

      // Remove individual filter chip
      if (e.target.matches('[data-remove-filter]') || e.target.closest('[data-remove-filter]')) {
        e.preventDefault();
        const button = e.target.matches('[data-remove-filter]') ? e.target : e.target.closest('[data-remove-filter]');
        const filterType = button.dataset.filterType;
        const filterValue = button.dataset.filterValue;
        this.removeFilter(filterType, filterValue);
      }

      // Close on overlay click
      if (e.target.matches('[data-filter-overlay]')) {
        this.closeFilterDrawer();
      }
    });

    // Filter checkbox changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-filter-checkbox]')) {
        this.handleFilterChange(e.target);
      }
    });

    // Sort dropdown change
    document.addEventListener('change', (e) => {
      if (e.target.matches('[data-sort-by]')) {
        this.handleSortChange(e.target.value);
      }
    });

    // ESC key to close drawer
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeFilterDrawer();
      }
    });
  },

  /**
   * Toggle desktop filter panel
   */
  toggleFilterPanel() {
    if (!this.filterPanel) return;

    const toggle = document.querySelector('[data-filter-toggle]');
    const isOpen = this.filterPanel.classList.contains('open');

    if (isOpen) {
      this.filterPanel.classList.remove('open');
      if (toggle) toggle.classList.remove('active');
    } else {
      this.filterPanel.classList.add('open');
      if (toggle) toggle.classList.add('active');
    }
  },

  /**
   * Open mobile filter drawer
   */
  openFilterDrawer() {
    if (!this.filterDrawer) return;

    this.filterDrawer.classList.add('open');
    if (this.filterOverlay) {
      this.filterOverlay.classList.add('active');
    }
    document.body.style.overflow = 'hidden';
  },

  /**
   * Close mobile filter drawer
   */
  closeFilterDrawer() {
    if (!this.filterDrawer) return;

    this.filterDrawer.classList.remove('open');
    if (this.filterOverlay) {
      this.filterOverlay.classList.remove('active');
    }
    document.body.style.overflow = '';
  },

  /**
   * Handle filter checkbox change
   */
  handleFilterChange(checkbox) {
    const filterType = checkbox.name;
    const filterValue = checkbox.value;

    if (checkbox.checked) {
      // Add filter
      if (!this.activeFilters[filterType]) {
        this.activeFilters[filterType] = [];
      }
      if (!this.activeFilters[filterType].includes(filterValue)) {
        this.activeFilters[filterType].push(filterValue);
      }
    } else {
      // Remove filter
      if (this.activeFilters[filterType]) {
        this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== filterValue);
        if (this.activeFilters[filterType].length === 0) {
          delete this.activeFilters[filterType];
        }
      }
    }

    this.applyFilters();
  },

  /**
   * Remove a specific filter
   */
  removeFilter(filterType, filterValue) {
    if (this.activeFilters[filterType]) {
      this.activeFilters[filterType] = this.activeFilters[filterType].filter(v => v !== filterValue);
      if (this.activeFilters[filterType].length === 0) {
        delete this.activeFilters[filterType];
      }
    }

    // Uncheck the checkbox
    const checkbox = document.querySelector(`input[name="${filterType}"][value="${filterValue}"]`);
    if (checkbox) {
      checkbox.checked = false;
    }

    this.applyFilters();
  },

  /**
   * Clear all active filters
   */
  clearAllFilters() {
    this.activeFilters = {};

    // Uncheck all checkboxes
    document.querySelectorAll('[data-filter-checkbox]:checked').forEach(checkbox => {
      checkbox.checked = false;
    });

    this.applyFilters();
  },

  /**
   * Apply filters and fetch filtered products
   */
  async applyFilters() {
    this.updateURL();
    this.updateUI();

    // Build filter URL
    const url = this.buildFilterURL();

    try {
      // Show loading state
      if (this.productGrid) {
        this.productGrid.classList.add('loading');
      }

      // Fetch filtered products
      const response = await fetch(url);
      const html = await response.text();

      // Parse response
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newGrid = doc.querySelector('#product-grid');

      if (newGrid && this.productGrid) {
        this.productGrid.innerHTML = newGrid.innerHTML;
        this.productGrid.classList.remove('loading');
      }

      // Update product count
      const productCount = doc.querySelector('[data-product-count]');
      const currentCount = document.querySelector('[data-product-count]');
      if (productCount && currentCount) {
        currentCount.textContent = productCount.textContent;
      }

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('filters:applied'));

    } catch (error) {
      console.error('Error applying filters:', error);
      if (this.productGrid) {
        this.productGrid.classList.remove('loading');
      }
    }
  },

  /**
   * Build filter URL with query parameters
   */
  buildFilterURL() {
    const params = new URLSearchParams(window.location.search);

    // Clear existing filter params
    params.delete('vendor');
    params.delete('size');
    params.delete('condition');
    params.delete('price');

    // Add active filters
    Object.keys(this.activeFilters).forEach(filterType => {
      this.activeFilters[filterType].forEach(value => {
        params.append(filterType, value);
      });
    });

    // Build URL
    const baseUrl = window.location.pathname;
    const queryString = params.toString();

    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  },

  /**
   * Update URL with filter parameters
   */
  updateURL() {
    const url = this.buildFilterURL();
    window.history.pushState({}, '', url);
  },

  /**
   * Parse URL parameters to set active filters
   */
  parseUrlFilters() {
    const params = new URLSearchParams(window.location.search);

    // Parse each filter type
    ['vendor', 'size', 'condition', 'price'].forEach(filterType => {
      const values = params.getAll(filterType);
      if (values.length > 0) {
        this.activeFilters[filterType] = values;

        // Check corresponding checkboxes
        values.forEach(value => {
          const checkbox = document.querySelector(`input[name="${filterType}"][value="${value}"]`);
          if (checkbox) {
            checkbox.checked = true;
          }
        });
      }
    });
  },

  /**
   * Update UI elements (filter count, chips, etc.)
   */
  updateUI() {
    // Update filter count
    const filterCount = Object.values(this.activeFilters).reduce((total, arr) => total + arr.length, 0);
    document.querySelectorAll('[data-filter-count]').forEach(el => {
      el.textContent = filterCount;
      el.style.display = filterCount > 0 ? 'inline-flex' : 'none';
    });

    // Update active filter chips
    this.updateFilterChips();

    // Show/hide clear all button
    const clearAllButtons = document.querySelectorAll('[data-clear-all]');
    clearAllButtons.forEach(btn => {
      if (filterCount > 0) {
        btn.classList.remove('hidden');
      } else {
        btn.classList.add('hidden');
      }
    });
  },

  /**
   * Update active filter chips
   */
  updateFilterChips() {
    const container = document.querySelector('[data-active-filters]');
    if (!container) return;

    container.innerHTML = '';

    Object.keys(this.activeFilters).forEach(filterType => {
      this.activeFilters[filterType].forEach(value => {
        const chip = document.createElement('div');
        chip.className = 'filter-chip';
        chip.innerHTML = `
          <span>${this.getFilterLabel(filterType, value)}</span>
          <button
            class="filter-chip-remove"
            data-remove-filter
            data-filter-type="${filterType}"
            data-filter-value="${value}"
            aria-label="Remove filter"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        `;
        container.appendChild(chip);
      });
    });
  },

  /**
   * Get human-readable label for filter
   */
  getFilterLabel(filterType, value) {
    // This could be enhanced to use actual product data labels
    return `${filterType}: ${value}`;
  },

  /**
   * Handle sort change
   */
  handleSortChange(sortValue) {
    const params = new URLSearchParams(window.location.search);

    if (sortValue && sortValue !== 'manual') {
      params.set('sort_by', sortValue);
    } else {
      params.delete('sort_by');
    }

    const url = params.toString() ? `${window.location.pathname}?${params.toString()}` : window.location.pathname;
    window.location.href = url;
  },
};

// ===== PRODUCT PAGE MODULE =====

const ProductPage = {
  /**
   * Initialize product page functionality
   */
  init() {
    this.mainImage = document.querySelector('[data-main-image]');
    this.thumbnails = document.querySelectorAll('[data-thumbnail]');
    this.variantSelector = document.querySelector('[data-variant-selector]');
    this.sizeSelector = document.querySelector('[data-size-selector]');
    this.productForm = document.querySelector('[data-product-form]');
    this.addToCartBtn = document.querySelector('[data-add-to-cart]');
    this.variantIdInput = document.querySelector('[data-variant-id]');
    this.productPrice = document.querySelector('[data-product-price]');

    if (!this.mainImage && !this.variantSelector && !this.sizeSelector) return;

    this.bindEvents();
  },

  /**
   * Bind product page events
   */
  bindEvents() {
    // Image gallery - thumbnail clicks
    this.thumbnails.forEach(thumbnail => {
      thumbnail.addEventListener('click', (e) => {
        this.handleThumbnailClick(e.currentTarget);
      });
    });

    // Variant selector change (dropdown)
    if (this.variantSelector) {
      this.variantSelector.addEventListener('change', (e) => {
        this.handleVariantChange(e.target.value);
      });
    }

    // Size selector buttons
    if (this.sizeSelector) {
      this.sizeSelector.addEventListener('click', (e) => {
        const button = e.target.closest('.size-option');
        if (!button || button.disabled) return;

        // Update active state
        this.sizeSelector.querySelectorAll('.size-option').forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        // Trigger variant change
        this.handleVariantChange(button.dataset.variantId);
      });
    }

    // Note: Form submission is handled by Cart module via click delegation on [data-add-to-cart]
    // No separate form submit handler needed

    // Copy link button
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-copy-link]') || e.target.closest('[data-copy-link]')) {
        e.preventDefault();
        this.copyProductLink();
      }
    });
  },

  /**
   * Handle thumbnail click
   */
  handleThumbnailClick(thumbnail) {
    const imageId = thumbnail.dataset.imageId;

    // Remove active class from all thumbnails
    this.thumbnails.forEach(t => t.classList.remove('active'));

    // Add active class to clicked thumbnail
    thumbnail.classList.add('active');

    // Update main image
    // In a real implementation, you would get the full image URL from a data attribute
    // For now, we'll just demonstrate the UI update
    const thumbnailImg = thumbnail.querySelector('img');
    if (thumbnailImg && this.mainImage) {
      const newSrc = thumbnailImg.src.replace('width=200', 'width=1200');
      this.mainImage.src = newSrc;
      this.mainImage.srcset = `
        ${thumbnailImg.src.replace('width=200', 'width=600')} 600w,
        ${thumbnailImg.src.replace('width=200', 'width=1200')} 1200w,
        ${thumbnailImg.src.replace('width=200', 'width=1800')} 1800w
      `;
    }
  },

  /**
   * Handle variant selection change
   */
  handleVariantChange(variantId) {
    if (!variantId) return;

    // Fetch variant data
    fetch(`${window.location.pathname}?variant=${variantId}`)
      .then(response => response.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update price (use innerHTML to preserve sale price markup)
        const newPrice = doc.querySelector('[data-product-price]');
        if (newPrice && this.productPrice) {
          this.productPrice.innerHTML = newPrice.innerHTML;
        }

        // Update variant ID in form
        if (this.variantIdInput) {
          this.variantIdInput.value = variantId;
        }

        // Update Add to Cart button
        if (this.addToCartBtn) {
          const newBtn = doc.querySelector('[data-add-to-cart]');
          if (newBtn) {
            this.addToCartBtn.textContent = newBtn.textContent;
            this.addToCartBtn.disabled = newBtn.disabled;
            this.addToCartBtn.dataset.variantId = variantId;
          }
        }

        // Update payment button visibility
        const newPaymentBtn = doc.querySelector('.shopify-payment-button');
        const currentPaymentBtn = document.querySelector('.shopify-payment-button');
        if (currentPaymentBtn) {
          currentPaymentBtn.style.display = newPaymentBtn ? '' : 'none';
        }

        // Update URL without page reload
        const url = new URL(window.location);
        url.searchParams.set('variant', variantId);
        window.history.replaceState({}, '', url);
      })
      .catch(error => {
        console.error('Error fetching variant:', error);
      });
  },

  /**
   * Handle add to cart
   */
  handleAddToCart() {
    const variantId = this.variantIdInput ? this.variantIdInput.value : this.addToCartBtn.dataset.variantId;

    if (!variantId) {
      console.error('No variant ID found');
      return;
    }

    // Show loading state
    if (this.addToCartBtn) {
      this.addToCartBtn.classList.add('loading');
      this.addToCartBtn.disabled = true;
    }

    // Use the Cart module to add item
    if (window.SupplyStudios && window.SupplyStudios.Cart) {
      window.SupplyStudios.Cart.addItem(variantId)
        .then(() => {
          // Reset button state
          if (this.addToCartBtn) {
            this.addToCartBtn.classList.remove('loading');
            this.addToCartBtn.disabled = false;
          }
        })
        .catch(error => {
          console.error('Error adding to cart:', error);
          if (this.addToCartBtn) {
            this.addToCartBtn.classList.remove('loading');
            this.addToCartBtn.disabled = false;
          }
        });
    }
  },

  /**
   * Copy product link to clipboard
   */
  copyProductLink() {
    const url = window.location.href;

    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
        .then(() => {
          // Show success message (could be enhanced with a toast notification)
          alert('Link copied to clipboard!');
        })
        .catch(err => {
          console.error('Failed to copy link:', err);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = url;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link:', err);
      }
      document.body.removeChild(textArea);
    }
  },
};

// ===== CART DRAWER =====

const CartDrawer = {
  /**
   * Initialize cart drawer
   */
  init() {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;

    // Open drawer when clicking cart link in header
    const cartLinks = document.querySelectorAll('[data-cart-link]');
    cartLinks.forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close drawer buttons - use event delegation so it works after content refresh
    drawer.addEventListener('click', (e) => {
      if (e.target.matches('[data-cart-close]') || e.target.closest('[data-cart-close]')) {
        e.preventDefault();
        this.close();
      }
    });

    // Close on overlay click
    const overlay = document.querySelector('[data-cart-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => this.close());
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('open')) {
        this.close();
      }
    });

    // Handle quantity changes
    drawer.addEventListener('change', (e) => {
      if (e.target.matches('[data-cart-quantity-input]')) {
        this.updateQuantity(e.target);
      }
    });

    // Handle remove buttons
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('[data-cart-remove]')) {
        e.preventDefault();
        const button = e.target.closest('[data-cart-remove]');
        const line = button.dataset.lineItem;
        this.removeItem(line);
      }
    });
  },

  /**
   * Open cart drawer
   */
  open() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');

    if (drawer) {
      drawer.classList.add('open');
      document.body.style.overflow = 'hidden';
    }

    if (overlay) {
      overlay.classList.add('active');
    }
  },

  /**
   * Close cart drawer
   */
  close() {
    const drawer = document.querySelector('[data-cart-drawer]');
    const overlay = document.querySelector('[data-cart-overlay]');

    if (drawer) {
      drawer.classList.remove('open');
      document.body.style.overflow = '';
    }

    if (overlay) {
      overlay.classList.remove('active');
    }
  },

  /**
   * Update item quantity
   */
  async updateQuantity(input) {
    const line = input.dataset.lineItem;
    const quantity = parseInt(input.value);

    if (quantity < 1) {
      this.removeItem(line);
      return;
    }

    try {
      await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity,
        }),
      });

      await Cart.refreshCartDrawer();
    } catch (error) {
      console.error('Error updating quantity:', error);
      alert('Error updating cart. Please try again.');
    }
  },

  /**
   * Remove item from cart
   */
  async removeItem(line) {
    try {
      await fetchJSON('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0,
        }),
      });

      await Cart.refreshCartDrawer();
    } catch (error) {
      console.error('Error removing item:', error);
      alert('Error removing item. Please try again.');
    }
  },
};

// ===== SCROLL POSITION RESTORATION =====

const ScrollRestoration = {
  /**
   * Initialize scroll position restoration
   */
  init() {
    // Enable browser scroll restoration
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    // Save scroll position when clicking product links
    this.saveScrollOnProductClick();

    // Restore scroll position on page load
    this.restoreScroll();
  },

  /**
   * Save scroll position when clicking product links
   */
  saveScrollOnProductClick() {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('a[href*="/products/"]');
      if (link) {
        const scrollPos = window.scrollY || document.documentElement.scrollTop;
        const currentPath = window.location.pathname;

        // Save scroll position for current page
        sessionStorage.setItem(`scroll_${currentPath}`, scrollPos.toString());
      }
    });
  },

  /**
   * Restore scroll position on page load
   */
  restoreScroll() {
    const currentPath = window.location.pathname;
    const savedScroll = sessionStorage.getItem(`scroll_${currentPath}`);

    if (savedScroll !== null) {
      // Wait for images to load before scrolling
      if (document.readyState === 'complete') {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(`scroll_${currentPath}`);
      } else {
        window.addEventListener('load', () => {
          window.scrollTo(0, parseInt(savedScroll));
          sessionStorage.removeItem(`scroll_${currentPath}`);
        });
      }
    }
  },
};

// ===== LAZY LOADING (Intersection Observer) =====

const LazyLoad = {
  /**
   * Initialize lazy loading for images
   */
  init() {
    // Check if native lazy loading is supported
    if ('loading' in HTMLImageElement.prototype) {
      // Native lazy loading supported - nothing to do
      return;
    }

    // Use Intersection Observer for older browsers
    const images = document.querySelectorAll('img[loading="lazy"]');

    if (!images.length) return;

    const imageObserver = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const img = entry.target;
          img.src = img.dataset.src || img.src;
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
          }
          img.removeAttribute('loading');
          observer.unobserve(img);
        }
      });
    });

    images.forEach((img) => imageObserver.observe(img));
  },
};

// ===== IMAGE ZOOM MODULE =====

const ImageZoom = {
  zoomModal: null,
  zoomImage: null,

  /**
   * Initialize image zoom functionality
   */
  init() {
    this.createZoomModal();
    this.bindEvents();
  },

  /**
   * Create zoom modal elements
   */
  createZoomModal() {
    // Create modal HTML
    const modal = document.createElement('div');
    modal.className = 'image-zoom-modal';
    modal.innerHTML = `
      <div class="image-zoom-overlay" data-zoom-close></div>
      <div class="image-zoom-container">
        <img class="image-zoom-img" src="" alt="" />
        <button class="image-zoom-close" data-zoom-close aria-label="Close">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(modal);
    this.zoomModal = modal;
    this.zoomImage = modal.querySelector('.image-zoom-img');
  },

  /**
   * Bind zoom events
   */
  bindEvents() {
    // Click on product images to zoom
    document.addEventListener('click', (e) => {
      const img = e.target.closest('.product-main-image img, .product-thumbnail img');
      if (img) {
        e.preventDefault();
        this.openZoom(img.src, img.alt);
      }
    });

    // Close zoom
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-zoom-close]')) {
        this.closeZoom();
      }
    });

    // ESC key to close
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.zoomModal.classList.contains('active')) {
        this.closeZoom();
      }
    });
  },

  /**
   * Open zoom view
   */
  openZoom(src, alt) {
    this.zoomImage.src = src.replace(/_(small|medium|large|compact|grande)\./, '_2048x.');
    this.zoomImage.alt = alt;
    this.zoomModal.classList.add('active');
    document.body.style.overflow = 'hidden';
  },

  /**
   * Close zoom view
   */
  closeZoom() {
    this.zoomModal.classList.remove('active');
    document.body.style.overflow = '';
  },
};

// ===== INITIALIZATION =====

/**
 * Initialize all modules when DOM is ready
 */
function initTheme() {
  // Initialize modules
  Cart.init();
  CartDrawer.init();
  MobileNav.init();
  Search.init();
  Filters.init();
  ProductPage.init();
  ScrollRestoration.init();
  LazyLoad.init();
  ImageZoom.init();

  console.log('🚀 SUPPLY STUDIOS theme initialized');
}

// Run on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTheme);
} else {
  // DOM already loaded
  initTheme();
}

// Export for use in other scripts if needed
window.SupplyStudios = {
  Cart,
  CartDrawer,
  MobileNav,
  Search,
  Filters,
  ProductPage,
  ScrollRestoration,
  LazyLoad,
  ImageZoom,
};
